import { FullStory, init, isInitialized } from '@fullstory/browser';
import i18n from '../i18n/config';
import { useAuthStore } from '../stores/authStore';

const DEFAULT_ORG_ID = 'o-24M46E-na1';
const RAW_ORG_ID = import.meta.env.VITE_FULLSTORY_ORG_ID;
const ORG_ID =
  RAW_ORG_ID && RAW_ORG_ID !== 'your_fullstory_org_id_here' && RAW_ORG_ID.length > 3
    ? RAW_ORG_ID
    : DEFAULT_ORG_ID;

let identifiedUserId: string | null = null;

export function initFullstory(): void {
  if (isInitialized()) return;
  try {
    init({
      orgId: ORG_ID,
    });
  } catch (error) {
    console.warn('FullStory initialization failed:', error);
  }
}

function daysSince(iso?: string | null): number | undefined {
  if (!iso) return undefined;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return undefined;
  return Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24));
}

function getThemeStr(): string {
  try {
    if (typeof document !== 'undefined') {
      if (document.documentElement.classList.contains('dark')) return 'dark';
      if (document.documentElement.classList.contains('light')) return 'light';
    }
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('theme') ?? 'unknown';
    }
  } catch {
    /* noop */
  }
  return 'unknown';
}

function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return '';
  }
}

export function setFullstoryUser(): void {
  if (!isInitialized()) return;
  try {
    const auth = useAuthStore.getState();
    const effectiveUser = auth.impersonatedUser ?? auth.user;
    if (!effectiveUser) return;

    if (identifiedUserId === effectiveUser.id) return;

    const realUser = auth.user;
    const isImpersonating = auth.impersonatedUser != null;
    const org = effectiveUser.organization;
    const profile = effectiveUser.profile;
    const domains = effectiveUser.domains ?? [];

    const properties: Record<string, unknown> = {
      // --- Reserved Fullstory fields ---
      displayName: effectiveUser.name,
      email: effectiveUser.email,

      // --- User identity ---
      user_id_str: effectiveUser.id,
      role_str: effectiveUser.role,
      logto_id_str: effectiveUser.logto_id ?? '',
      is_active_bool: effectiveUser.is_active ?? true,
      avatar_url_str: effectiveUser.avatar_url ?? effectiveUser.avatar ?? '',
      created_at_date: effectiveUser.created_at ?? null,
      account_age_days_int: daysSince(effectiveUser.created_at),

      // --- Organization ---
      organization_id_str: effectiveUser.organization_id ?? '',
      organization_name_str: org?.name ?? '',
      organization_is_active_bool: effectiveUser.organization_is_active ?? org?.is_active ?? null,
      organization_sector_template_id_str: org?.sector_template_id ?? '',
      organization_created_at_date: org?.created_at ?? null,

      // --- Profile (feature-level RBAC) ---
      profile_id_str: effectiveUser.profile_id ?? '',
      profile_key_str: profile?.key ?? '',
      profile_name_str: profile?.name ?? '',
      profile_is_system_bool: profile?.is_system ?? null,

      // --- Domains ---
      domain_count_int: domains.length,
      domain_ids_strs: domains.map((d) => d.id),
      domain_names_strs: domains.map((d) => d.name),

      // --- Impersonation context ---
      is_impersonating_bool: isImpersonating,
      real_user_id_str: isImpersonating && realUser ? realUser.id : '',
      real_user_email_str: isImpersonating && realUser ? realUser.email : '',
      real_user_name_str: isImpersonating && realUser ? realUser.name : '',
      real_user_role_str: isImpersonating && realUser ? realUser.role : '',

      // --- App / environment ---
      app_version_str: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '',
      app_mode_str: import.meta.env.MODE,
      locale_str: i18n.language ?? '',
      browser_locale_str: typeof navigator !== 'undefined' ? navigator.language : '',
      timezone_str: getTimezone(),
      theme_str: getThemeStr(),
      screen_width_int: typeof window !== 'undefined' ? window.screen.width : 0,
      screen_height_int: typeof window !== 'undefined' ? window.screen.height : 0,
      viewport_width_int: typeof window !== 'undefined' ? window.innerWidth : 0,
      viewport_height_int: typeof window !== 'undefined' ? window.innerHeight : 0,
      device_pixel_ratio_real: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
      referrer_str: typeof document !== 'undefined' ? document.referrer : '',
    };

    FullStory('setIdentity', {
      uid: effectiveUser.id,
      properties,
    });
    identifiedUserId = effectiveUser.id;
  } catch (error) {
    console.warn('FullStory identify error:', error);
  }
}

export function clearFullstoryUser(): void {
  if (!isInitialized()) return;
  try {
    FullStory('setIdentity', { anonymous: true });
    identifiedUserId = null;
  } catch (error) {
    console.warn('FullStory anonymize error:', error);
  }
}
